import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, of, from, forkJoin, firstValueFrom } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../environments/environments';
import { DevArea, FacilityType, FacilityName, GroupUser, FeLookupResponse, FilterPayload, FeCapacitiesFilterRequest, FeCapacitiesFilterResponse, FeCapacityRequest, FeTableItem, FeCapacityResponse, DeleteFeCapacityRequest } from './interfaces/table.interfaces';
import { ErrorHandler, ApiResultStatus } from '../utils/error-handler';

@Injectable({
  providedIn: 'root',
})
export class TableService {
  private baseUrl = environment.WebApiURL;
  private readonly feCapacitiesExportTabName = 'FECapacities';

  private get feCapacitiesUrl() {
    return `${this.baseUrl}/api/CMPFE/GetFacilityDetails`;}

  private get feLookupUrl() {
      return `${this.baseUrl}/api/CMPFE/GetApplicationFiltersMulti`;}

  private get facilityTypeAdminUrl() {
        return `${this.baseUrl}/api/CMPFE/GetFacilityTypeAdminDetails`;}

  private get devAreaAdminUrl() {
          return `${this.baseUrl}/api/CMPFE/GetDevAreaAdminDetails`;}  
  private get facilityNameAdminUrl() {
          return `${this.baseUrl}/api/CMPFE/GetFacilityNameAdminDetails`;} 
  // private get ipsomaxUrl() {
  //         return `${this.baseUrl}/api/CMPFE/GetIpsomaxDetails`;}
  private get exportUrl() {
          return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;}

  
  private get feCapacitiesFiltersUrl() {
          return `${this.baseUrl}/api/CMPFE/GetFECapacitiesFilters`;} 
  private get deletefacilityTypeAdminUrl() {
          return `${this.baseUrl}/api/CMPFE/DeleteFacilityTypeAdmin`;} 
  private get insertUpdateDevAreaUrl() {
          return `${this.baseUrl}/api/CMPFE/InsertUpdateDevAreaAdmin`;}   
  private get insertOrUpdateFacilityNameUrl() {
          return `${this.baseUrl}/api/CMPFE/InsertUpdateFacilityNameAdmin`;} 
  private get insertOrUpdatefacilityTypeUrl() {
          return `${this.baseUrl}/api/CMPFE/InsertUpdateFacilityTypeAdmin`;}   
  private get deleteDevAreaAdminUrl() {
          return `${this.baseUrl}/api/CMPFE/DeleteDevAreaAdmin`;} 
  private get insertFeCapacityUrl() {
            return `${this.baseUrl}/api/CMPFE/InsertUpdateMasterFEAds`;}
  private get deletefacilityDetailsUrl(){
          return `${this.baseUrl}/api/CMPFE/DeleteFECapacity`;}        
  private azureGroupId = 'a44d00f3-ad3d-4bb9-9189-b1fb28966540';
  constructor(private http: HttpClient, private msalService: MsalService) {}

private getExportFileResponse(httpResponse: HttpResponse<Blob>, fallbackBaseName: string): { blob: Blob; fileName: string } {
  const blob = httpResponse.body;
  if (!blob) {
    throw new Error('Export returned an empty file.');
  }

  const contentDisposition = httpResponse.headers.get('content-disposition') || '';
  const contentType = httpResponse.headers.get('content-type') || blob.type || '';
  const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
  const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
  const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
  const normalizedContentType = contentType.toLowerCase();
  const inferredExtension =
    normalizedContentType.includes('spreadsheetml') ||
    normalizedContentType.includes('xlsx') ||
    decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
      ? '.xlsx'
      : '.csv';
  const fallbackFileName = `${fallbackBaseName}${inferredExtension}`;
  const rawFileName = rawHeaderFileName || fallbackFileName;
  const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
  const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
    ? decodedFileName
    : `${decodedFileName}${inferredExtension}`;

  return {
    blob,
    fileName,
  };
}

public async exportDataByTabName(tabName: string, fallbackBaseName?: string): Promise<{ blob: Blob; fileName: string }> {
  const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  const httpResponse = await firstValueFrom(
    this.http.post(this.exportUrl, { tabName }, {
      headers,
      observe: 'response' as const,
      responseType: 'blob' as const,
    })
  );

  return this.getExportFileResponse(httpResponse, fallbackBaseName || tabName);
}

public async getFeCapacitiesData(payload: FeCapacityRequest): Promise<FeCapacityResponse> {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      return firstValueFrom(
       this.http.post<FeCapacityResponse>(this.feCapacitiesUrl, payload, { headers, observe: 'response' as const }).pipe(
       map((httpResponse: HttpResponse<FeCapacityResponse>) => {
         // NEW: Extract HTTP status code from response header
         const httpStatusCode = httpResponse.status;
         const body = httpResponse.body;
         const parsed = ErrorHandler.parseHttpResponse(httpResponse);

         // OLD/FALLBACK: Body-based status extraction — TODO: Remove once header approach is verified
         // const parsed = ErrorHandler.parseApiResult(response);

         return {
           ...body,
           statusCode: httpStatusCode, // Uses HTTP header status code
           // statusCode: parsed.statusCode, // OLD: from response body — TODO: Remove
           statusMessage: parsed.message,
           fetable: body?.fetable || [],
           nextRowNumber: body?.nextRowNumber || 0
         } as FeCapacityResponse;
       }),
         catchError((err) => {
         // NEW: Extract HTTP status code from error response
         const httpStatusCode = err?.status || 500;
         const parsed = ErrorHandler.parseApiResult(err?.error || err);
         // OLD/FALLBACK: — TODO: Remove once header approach is verified
         // return of({ statusCode: parsed.statusCode || err?.status || 500, ... });
         return of({ statusCode: httpStatusCode, statusMessage: parsed.message, fetable: [], nextRowNumber: 0 } as FeCapacityResponse);
         })
         )
       );
    }

public async exportAllFeCapacitiesData(): Promise<{ blob: Blob; fileName: string }> {
  return this.exportDataByTabName(this.feCapacitiesExportTabName, 'FECapacities');
}

public async saveFeCapacitiesData(data: any[]): Promise<any[]> {
  const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  const getSafeNumber = (val: any): number =>
    isNaN(parseFloat(val)) ? 0.0 : parseFloat(parseFloat(val).toFixed(2));
  const toNullOrTrim = (val: any): string | null => {
    if (val === undefined || val === null) return null;
    if (typeof val === 'string') {
      const trimmed = val.trim();
      return trimmed.length ? trimmed : null;
    }
    return val;
  };

  const results: any[] = [];

  for (const row of data) {
    const isNew = row.isNew;
    const o = row.original || {};
    const payload = {
      userEmail: toNullOrTrim(row.userEmail),

      oldDevelopmentAreaName: isNew ? null : toNullOrTrim(o.developmentArea),
      oldFacilityName: isNew ? null : toNullOrTrim(o.facilityName),
      oldFacilityTypeName: isNew ? null : toNullOrTrim(o.facilityType),
      oldTrainName: isNew ? null : toNullOrTrim(o.train),
      oldSelectedOil: isNew ? 0 : getSafeNumber(o.selectedOilCapacity),
      oldSelectedWater: isNew ? 0 : getSafeNumber(o.selectedWaterCapacity),
      oldSelectedGas: isNew ? 0 : getSafeNumber(o.selectedGasCapacity),
      oldPlannedOil: isNew ? 0 : getSafeNumber(o.plannedOilCapacity),
      oldPlannedWater: isNew ? 0 : getSafeNumber(o.plannedWaterCapacity),
      oldPlannedGas: isNew ? 0 : getSafeNumber(o.plannedGasCapacity),
      oldPermittedOilRate: isNew ? 0 : getSafeNumber(o.permittedOilRate),
      oldPermittedWaterRate: isNew ? 0 : getSafeNumber(o.permittedWaterRate),
      oldPermittedGasRate: isNew ? 0 : getSafeNumber(o.permittedGasRate),
      // oldCalculatedOil: isNew ? 0 : getSafeNumber(o.calculatedOilCapacity),
      // oldCalculatedWater: isNew ? 0 : getSafeNumber(o.calculatedWaterCapacity),
      // oldCalculatedGas: isNew ? 0 : getSafeNumber(o.calculatedGasCapacity),
      oldRfsdDate: isNew ? null : this.formatDateValue(o.rfsd),
      oldComments: isNew ? null : toNullOrTrim(o.comments),

      newDevelopmentAreaName: toNullOrTrim(row.developmentArea),
      newFacilityName: toNullOrTrim(row.facilityName),
      newFacilityTypeName: toNullOrTrim(row.facilityType),
      newTrainName: toNullOrTrim(row.train),
      newSelectedOil: getSafeNumber(row.selectedOilCapacity),
      newSelectedWater: getSafeNumber(row.selectedWaterCapacity),
      newSelectedGas: getSafeNumber(row.selectedGasCapacity),
      newPlannedOil: getSafeNumber(row.plannedOilCapacity),
      newPlannedWater: getSafeNumber(row.plannedWaterCapacity),
      newPlannedGas: getSafeNumber(row.plannedGasCapacity),
      newPermittedOilRate: getSafeNumber(row.permittedOilRate),
      newPermittedWaterRate: getSafeNumber(row.permittedWaterRate),
      newPermittedGasRate: getSafeNumber(row.permittedGasRate),
      // newCalculatedOil: getSafeNumber(row.calculatedOilCapacity),
      // newCalculatedWater: getSafeNumber(row.calculatedWaterCapacity),
      // newCalculatedGas: getSafeNumber(row.calculatedGasCapacity),
      newRfsdDate: this.formatDateValue(row.rfsd),
      newComments: toNullOrTrim(row.comments),
    };

    try {
      const httpResponse = await firstValueFrom(
        this.http.post<any>(this.insertFeCapacityUrl, payload, { headers, observe: 'response' as const })
      );

      // Prefer API body status code (business status) when present.
      const parsed = ErrorHandler.parseApiResult(httpResponse?.body ?? httpResponse);
      const finalStatusCode = parsed.statusCode || httpResponse.status;

      // OLD/FALLBACK: Body-based status extraction — TODO: Remove once header approach is verified
      // const res = await firstValueFrom(this.http.post<any>(this.insertFeCapacityUrl, payload, { headers }));
      // const parsed = ErrorHandler.parseApiResult(res);

      results.push({
        error: !parsed.success,
        statusCode: finalStatusCode,
        // statusCode: parsed.statusCode, // OLD: from response body — TODO: Remove
        statusMessage: parsed.message,
        facilityName: row.facilityName,
      });
    } catch (err: any) {
      // NEW: Extract HTTP status code from error response
      const httpStatusCode = err?.status || 500;
      const parsed = ErrorHandler.parseApiResult(err?.error || err);

      // OLD/FALLBACK: — TODO: Remove once header approach is verified
      // statusCode: parsed.statusCode || err?.status || 500,

      results.push({
        error: true,
        statusCode: httpStatusCode, // Uses HTTP header status code
        statusMessage: parsed.message,
        facilityName: row.facilityName,
      });
    }
  }

  return results;
}

private formatDateValue = (input: string): string | null => {
    if (!input || !/^\d{4}-\d{2}-\d{2}$/.test(input)) return null;
    const [year, month, day] = input.split('-');
    return `${month}/${day}/${year}`;
  };
// Fetches IPSOMAX details based on optional parameters like train and ipsomax name
// public async getIpsomaxDetails(ipsomaxName: string | null = null, trainName: string | null = null): Promise<any[]> {
//       const body = { ipsomaxName, trainName };
//       const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
//       try {
//         const res = await firstValueFrom(this.http.post<any>(this.ipsomaxUrl, body, { headers }));
//         return res?.ipsomaxTable || [];
//       } catch (err) {
//         console.error('Error loading IPSOMAX details:', err);
//         return [];
//       }
//     }
 // Fetches all lookup values including IPSOMAX and train names   
public async getFeLookupValuesAll(): Promise<FeLookupResponse> {
    const payload: FilterPayload = {
      devArea: null,
      facilityName: null,
      facilityType: null
    };
    try {
      const [response] = await Promise.all([
        firstValueFrom(this.http.post<any>(this.feLookupUrl, payload)),
       
      ]);
      const filters = response?.filters || [];
      const developmentArea = Array.from(
        new Set(filters.map((f: any) => f.developmentArea).filter(Boolean))
      ).map(String);
  
      const facilityName = Array.from(
        new Set(filters.map((f: any) => f.surfaceFacilityName).filter(Boolean))
      ).map(String);
  
      const facilityType = Array.from(
        new Set(filters.map((f: any) => f.surfaceFacilityTypeName).filter(Boolean))
      ).map(String);
  
      // const ipsomax = ipsomaxDetails
      //   ? Array.from(
      //       new Set(ipsomaxDetails.map((i: any) => i.ipsomaxName).filter(Boolean))
      //     ).map(String)
      //   : [];
  
        const train: string[] = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
  
      return {
        developmentArea,
        facilityName,
        facilityType,
        // ipsomax,
        train
      };
    } catch (err) {     
      return {
        developmentArea: [],
        facilityName: [],
        facilityType: [],
        ipsomax: [],
        train: []
      };
    }
  }
// Fetches filtered lookup data based on the provided payload
public async getFilteredLookupData(payload: FilterPayload): Promise<FeLookupResponse> {
    try {
      const response = await this.http.post<any>(this.feLookupUrl, payload).toPromise();
      const filters = response?.filters || [];
      return {
        developmentArea: Array.from(new Set(filters.map((f: any) => f.developmentArea).filter(Boolean))).map(String),
        facilityName: Array.from(new Set(filters.map((f: any) => f.surfaceFacilityName).filter(Boolean))).map(String),
        facilityType: Array.from(new Set(filters.map((f: any) => f.surfaceFacilityTypeName).filter(Boolean))).map(String),
        ipsomax: [],
        train: []
      };
    } catch (err) {
      return { developmentArea: [], facilityName: [], facilityType: [], train: [] };
    }
  }

  // Fetches FE Capacities filters based on the provided payload
  public async getFeCapacitiesFilters(payload: FeCapacitiesFilterRequest): Promise<FeCapacitiesFilterResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    try {
      const response = await firstValueFrom(
        this.http.post<FeCapacitiesFilterResponse>(this.feCapacitiesFiltersUrl, payload, { headers })
      );
      return response || [];
    } catch (err) {
      return [];
    }
  }

 // Fetches development area admin details 
public async getDevAreaAdminDetails(): Promise<DevArea[]> {
    try {
      const res = await firstValueFrom(this.http.get<any>(this.devAreaAdminUrl));
      return (res?.developmentAreas || []).map((item: any) => ({
        developmentArea: item.developmentArea,
        owner: item.owner? item.owner.split(',').reduce((acc: string[], curr: string, idx: number) => {
        if (idx % 2 === 0) {
        acc.push(curr.trim());
        } else {
        acc[acc.length - 1] += `, ${curr.trim()}`;}
        return acc;}, []): [],
         ownerEmail: item.ownerEmail || '',
        isActive: item.isActive,
        createdBy: item.createdBy,
        updatedBy: item.updatedBy || ''
      }));
    } catch (err) {      return [];
    }
  }
// Fetches facility type admin details
public async getFacilityTypeAdminDetails(): Promise<FacilityType[]> {
    try {
      const res = await firstValueFrom(this.http.get<any>(this.facilityTypeAdminUrl));
      return (res?.facilityTypes || []).map((item: any) => ({
        type: item.surfaceFacilityTypeName,
        createdBy: item.surfaceFacilityTypeCreatedBy,
        updatedBy: item.surfaceFacilityTypeUpdatedBy || ''
      }));
    } catch (err) {
      return [];
    }
  }
// Fetches facility name admin details
public getFacilityNameAdminDetails(): Observable<FacilityName[]> {
    return this.http.get<any>(this.facilityNameAdminUrl).pipe(
      map((res: any) => (res?.facilityNames || []).map((item: any) => ({
        developmentArea: item.developmentArea,
        type: item.surfaceFacilityTypeName,
        facilityName: item.surfaceFacilityName,
        createdBy: item.createdBy || null,
        updatedBy: item.updatedBy || null
      }))),
      catchError(err => {
        return of([]);
      })
    );
  }
// Inserts or updates a development area
public insertOrUpdateDevArea(payload: {
  developmentArea: string;
  owner: string;
  ownerEmail: string;
  userName: string;
  isActive: string;
}): Observable<any> {
  return this.http.post<any>(this.insertUpdateDevAreaUrl, payload, { observe: 'response' as const }).pipe(
    map((httpResponse: HttpResponse<any>) => {
      // NEW: Extract HTTP status code from response header
      const httpStatusCode = httpResponse.status;
      const body = httpResponse.body;
      const parsed = ErrorHandler.parseHttpResponse(httpResponse);

      // OLD/FALLBACK: Body-based status extraction — TODO: Remove once header approach is verified
      // const parsed = ErrorHandler.parseApiResult(res);

      return {
        statusMessage: parsed.message,
        statusCode: httpStatusCode, // Uses HTTP header status code
        // statusCode: parsed.statusCode, // OLD: from response body — TODO: Remove
        error: !parsed.success,
        ...body,
      };
    }),
    catchError((err) => {
      // NEW: Extract HTTP status code from error response
      const httpStatusCode = err?.status || 500;
      const parsed = ErrorHandler.parseApiResult(err?.error || err);

      // OLD/FALLBACK: — TODO: Remove once header approach is verified
      // statusCode: parsed.statusCode || err?.status || 500,

      return of({
        statusMessage: parsed.message,
        statusCode: httpStatusCode, // Uses HTTP header status code
        error: true,
      });
    })
  );
}
// Inserts or updates a facility type 
public insertOrUpdateFacilityType(payload: {
  existingFacilityTypeName: string;
  newFacilityTypeName: string;
  surfaceFacilityTypeCreatedBy: string;
  userName: string;
}): Observable<any> {
  return this.http.post<any>(this.insertOrUpdatefacilityTypeUrl, payload, { observe: 'response' as const }).pipe(
    map((httpResponse: HttpResponse<any>) => {
      // NEW: Extract HTTP status code from response header
      const httpStatusCode = httpResponse.status;
      const body = httpResponse.body;
      const parsed = ErrorHandler.parseHttpResponse(httpResponse);

      // OLD/FALLBACK: Body-based status extraction — TODO: Remove once header approach is verified
      // const parsed = ErrorHandler.parseApiResult(res);

      return {
        statusMessage: parsed.message,
        statusCode: httpStatusCode, // Uses HTTP header status code
        // statusCode: parsed.statusCode, // OLD: from response body — TODO: Remove
        error: !parsed.success,
        ...body,
      };
    }),
    catchError((err) => {
      // NEW: Extract HTTP status code from error response
      const httpStatusCode = err?.status || 500;
      const parsed = ErrorHandler.parseApiResult(err?.error || err);

      // OLD/FALLBACK: — TODO: Remove once header approach is verified
      // statusCode: parsed.statusCode || err?.status || 500,

      return of({
        statusMessage: parsed.message,
        statusCode: httpStatusCode, // Uses HTTP header status code
        error: true,
      });
    })
  );
}
// Inserts or updates a facility name
public insertOrUpdateFacilityName(payload: {
  existingFacilityName: string | null;
  newFacilityName: string;
  facilityTypeName: string;
  developmentArea: string;
  userName: string;
}): Observable<any> {
  const url = this.insertOrUpdateFacilityNameUrl;
  const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  return this.http.post<any>(url, payload, { headers, observe: 'response' as const }).pipe(
    map((httpResponse: HttpResponse<any>) => {
      // NEW: Extract HTTP status code from response header
      const httpStatusCode = httpResponse.status;
      const body = httpResponse.body;
      const parsed = ErrorHandler.parseHttpResponse(httpResponse);

      // OLD/FALLBACK: Body-based status extraction — TODO: Remove once header approach is verified
      // const parsed = ErrorHandler.parseApiResult(res);

      return {
        statusMessage: parsed.message,
        statusCode: httpStatusCode, // Uses HTTP header status code
        // statusCode: parsed.statusCode, // OLD: from response body — TODO: Remove
        error: !parsed.success,
        ...body
      };
    }),
    catchError((error) => {
      // NEW: Extract HTTP status code from error response
      const httpStatusCode = error?.status || 500;
      const parsed = ErrorHandler.parseApiResult(error?.error || error);

      // OLD/FALLBACK: — TODO: Remove once header approach is verified
      // statusCode: parsed.statusCode || error?.status || 500,

      return of({
        statusMessage: parsed.message,
        statusCode: httpStatusCode, // Uses HTTP header status code
        error: true
      });
    })
  );
}
// Deletes a development area admin record
public deleteDevAreaAdmin(developmentArea: string): Observable<any> {
    const url = this.deleteDevAreaAdminUrl;
 
    return this.http
      .request<any>('POST', url, {
        body: { developmentArea },
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response' as const,
      })
      .pipe(
        map((httpResponse: HttpResponse<any>) => {
          // NEW: Extract HTTP status code from response header
          const httpStatusCode = httpResponse.status;
          const body = httpResponse.body;
          const parsed = ErrorHandler.parseHttpResponse(httpResponse);

          // OLD/FALLBACK: Body-based status extraction — TODO: Remove once header approach is verified
          // const parsed = ErrorHandler.parseApiResult(res);

          return {
            statusMessage: parsed.message,
            statusCode: httpStatusCode, // Uses HTTP header status code
            // statusCode: parsed.statusCode, // OLD: from response body — TODO: Remove
            error: !parsed.success,
            ...body
          };
        }),
        catchError((err) => {
    
          const httpStatusCode = err?.status || 500;
          const parsed = ErrorHandler.parseApiResult(err?.error || err);

          // OLD/FALLBACK: — TODO: Remove once header approach is verified
          // statusCode: parsed.statusCode || err?.status || 500,

          return of({
            error: true,
            statusCode: httpStatusCode, // Uses HTTP header status code
            statusMessage: parsed.message
          });
        })
      );
  }
// Deletes a facility type admin record
public deletefacilityTypeAdmin(facilityType: string): Observable<any> {
    const url = this.deletefacilityTypeAdminUrl;
 
    return this.http
  .request<any>('POST', url, {
    body: { surfaceFacilityTypeName: facilityType },
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    observe: 'response' as const,
  })
  .pipe(
    map((httpResponse: HttpResponse<any>) => {
      // NEW: Extract HTTP status code from response header
      const httpStatusCode = httpResponse.status;
      const body = httpResponse.body;
      const parsed = ErrorHandler.parseHttpResponse(httpResponse);

      // OLD/FALLBACK: Body-based status extraction — TODO: Remove once header approach is verified
      // const parsed = ErrorHandler.parseApiResult(res);

      return {
        statusMessage: parsed.message,
        statusCode: httpStatusCode, // Uses HTTP header status code
        // statusCode: parsed.statusCode, // OLD: from response body — TODO: Remove
        error: !parsed.success,
        ...body
      };
    }),
    catchError((err) => {
      // NEW: Extract HTTP status code from error response
      const httpStatusCode = err?.status || 500;
      const parsed = ErrorHandler.parseApiResult(err?.error || err);

      // OLD/FALLBACK: — TODO: Remove once header approach is verified
      // statusCode: parsed.statusCode || err?.status || 500,

      return of({
        error: true,
        statusCode: httpStatusCode, // Uses HTTP header status code
        statusMessage: parsed.message,
      });
    })
  );
}
// Deletes a facility capacity record
public async deleteFeCapacity(row: any): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const getSafeNumber = (val: any): number =>
      isNaN(parseFloat(val)) ? 0.0 : parseFloat(parseFloat(val).toFixed(2));
    const payload: DeleteFeCapacityRequest = {
      userEmail: row.userEmail ?? null,
      developmentArea: row.developmentArea ?? null,
      surfaceFacilityName: row.facilityName ?? null,
      surfaceFacilityTypeName: row.facilityType ?? null,
      trainName: row.train ?? null,
      selectedOil: getSafeNumber(row.selectedOilCapacity),
      selectedWater: getSafeNumber(row.selectedWaterCapacity),
      selectedGas: getSafeNumber(row.selectedGasCapacity),
      plannedOil: getSafeNumber(row.plannedOilCapacity),
      plannedWater: getSafeNumber(row.plannedWaterCapacity),
      plannedGas: getSafeNumber(row.plannedGasCapacity),
      rfsdDate: this.formatDateValue(row.rfsd),
    };
    try {
      const httpResponse = await firstValueFrom(this.http.request<any>('POST', this.deletefacilityDetailsUrl, {
        body: payload,
        headers,
        observe: 'response' as const,
      }));

      // NEW: Extract HTTP status code from response header
      const httpStatusCode = httpResponse.status;
      const body = httpResponse.body;
      const parsed = ErrorHandler.parseHttpResponse(httpResponse);

      // OLD/FALLBACK: Body-based status extraction — TODO: Remove once header approach is verified
      // const parsed = ErrorHandler.parseApiResult(res);

      return {
        statusMessage: parsed.message,
        statusCode: httpStatusCode, // Uses HTTP header status code
        // statusCode: parsed.statusCode, // OLD: from response body — TODO: Remove
        error: !parsed.success,
        ...body
      };
    } catch (err: any) {
      // NEW: Extract HTTP status code from error response
      const httpStatusCode = err?.status || 500;
      const parsed = ErrorHandler.parseApiResult(err?.error || err);

      // OLD/FALLBACK: — TODO: Remove once header approach is verified
      // statusCode: parsed.statusCode || err?.status || 500,

      return {
        error: true,
        statusCode: httpStatusCode, // Uses HTTP header status code
        statusMessage: parsed.message
      };
    }
  }
// Fetches users from a specific Azure AD group using Microsoft Graph API
public async getAzureGroupUsers(): Promise<GroupUser[]> {
    const graphUrl = `https://graph.microsoft.com/v1.0/groups/${this.azureGroupId}/members`;
    const account = this.msalService.instance.getActiveAccount();
    if (!account) return [];
  
    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        scopes: ['GroupMember.Read.All'],
        account: account
      });
  
      const headers = new HttpHeaders({ Authorization: `Bearer ${result.accessToken}` });
      const response = await this.http.get<any>(graphUrl, { headers }).toPromise();
  
      return (response?.value || []).map((user: any) => ({
        name: user.displayName,
        email: user.mail || user.userPrincipalName
      }));
    } catch (err) {
      return [];
    }
  }
}
export type {DevArea,FacilityType,FacilityName,GroupUser,FeLookupResponse, FilterPayload,FeCapacitiesFilterRequest,FeCapacitiesFilterResponse,} from './interfaces/table.interfaces';
